import React, { useRef, useEffect, useState } from 'react';

import {
  Search,
  User,
  UserCheck,
  UserX,
  Edit,
  Key,
  Download,
  Filter,
  ChevronDown,
  Plus
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

interface UserData {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  role: 'agent' | 'formateur' | 'admin';
  is_active: boolean;
  created_at: string;
  promotions: any[];
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | HTMLTableCellElement | null>(null);
  const [openUpward, setOpenUpward] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    prenom: '',
    nom: '',
    role: 'agent',
    password: '',
    confirmPassword: ''
  });

  const [editFormData, setEditFormData] = useState({
    prenom: '',
    nom: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const dropdowns = document.querySelectorAll('.dropdown-menu');
      if (![...dropdowns].some(d => d.contains(e.target as Node))) {
        setShowRoleMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par rôle
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let pwd = '';
    for (let i = 0; i < 6; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pwd;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      await api.post('/admin/users', {
        email: formData.email,
        nom: formData.nom,
        prenom: formData.prenom,
        role: formData.role,
        password: formData.password
      });
      toast.success('Utilisateur créé');
      setShowCreateModal(false);
      setFormData({ email: '', prenom: '', nom: '', role: 'agent', password: '', confirmPassword: '' });
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const toggleUserStatus = async (userId: number) => {
    try {
      await api.put(`/admin/users/${userId}/status`);
      toast.success('Statut modifié');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la modification du statut');
    }
  };

  const updateUserRole = async (userId: number, newRole: string) => {
    try {
      await api.put(`/admin/users/${userId}/role`, null, {
        params: { role: newRole },
      });

      toast.success('Rôle modifié');
      fetchUsers();
      setShowEditModal(false);
      setShowRoleMenu(false);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification du rôle');
    }
  };

  const openEditModal = (user: UserData) => {
    setSelectedUser(user);
    setEditFormData({ prenom: user.prenom, nom: user.nom, password: '', confirmPassword: '' });
    setShowEditModal(true);
    setShowRoleMenu(false);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editFormData.password && editFormData.password !== editFormData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      const payload: any = {
        nom: editFormData.nom,
        prenom: editFormData.prenom,
      };
      if (editFormData.password) {
        payload.password = editFormData.password;
      }
      await api.put(`/admin/users/${selectedUser?.id}`, payload);
      toast.success('Utilisateur mis à jour');
      setShowEditModal(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const exportUsers = async () => {
    try {
      const response = await api.get('/admin/export/users', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export réussi');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'formateur':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h1>
          <p className="mt-1 text-gray-600">
            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => {
              const pwd = generatePassword();
              setFormData({ email: '', prenom: '', nom: '', role: 'agent', password: pwd, confirmPassword: pwd });
              setShowCreateModal(true);
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvel utilisateur
          </button>
          <button
            onClick={exportUsers}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Filtre par rôle */}
          <div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            >
              <option value="all">Tous les rôles</option>
              <option value="agent">Agents</option>
              <option value="formateur">Formateurs</option>
              <option value="admin">Administrateurs</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste des utilisateurs : Desktop (tableau) */}
      <div className="hidden md:block bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscrit le
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.prenom} {user.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {user.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <UserCheck className="h-3 w-3 mr-1" />
                        Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <UserX className="h-3 w-3 mr-1" />
                        Inactif
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative z-20"
                    ref={selectedUser?.id === user.id ? (dropdownRef as React.RefObject<HTMLTableCellElement>) : null}
                  >
                    <div className="inline-block text-left">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowRoleMenu(prev => (selectedUser?.id === user.id ? !prev : true));

                          setTimeout(() => {
                            const dropdownRect = dropdownRef.current?.getBoundingClientRect();
                            const spaceBelow = window.innerHeight - (dropdownRect?.bottom || 0);
                            setOpenUpward(spaceBelow < 200);
                          }, 0);
                        }}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        <ChevronDown className="h-5 w-5" />
                      </button>

                      {showRoleMenu && selectedUser?.id === user.id && (
                        <div className={`dropdown-menu absolute right-0 ${openUpward ? 'bottom-full mb-2' : 'mt-2'} w-56 origin-top-right rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}>
                          <div className="py-1">
                            <button
                              onClick={() => openEditModal(user)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Modifier
                            </button>
                            <button
                              onClick={() => updateUserRole(user.id, 'agent')}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Définir comme Agent
                            </button>
                            <button
                              onClick={() => updateUserRole(user.id, 'formateur')}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Définir comme Formateur
                            </button>
                            <button
                              onClick={() => updateUserRole(user.id, 'admin')}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Définir comme Admin
                            </button>
                            <hr className="my-1" />
                            <button
                              onClick={() => toggleUserStatus(user.id)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              {user.is_active ? 'Désactiver' : 'Activer'} le compte
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Liste des utilisateurs : Mobile (cartes) */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => (
          <div key={user.id} className="bg-white shadow rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {user.prenom} {user.nom}
                  </h3>
                  <p className="text-sm text-gray-600 break-words">{user.email}</p>
                </div>
              </div>

              <div
                className="relative"
                ref={selectedUser?.id === user.id ? (dropdownRef as React.RefObject<HTMLDivElement>) : null}
              >
                <button
                  onClick={() => {
                    setSelectedUser(user);
                    setShowRoleMenu(prev => (selectedUser?.id === user.id ? !prev : true));
                    setTimeout(() => {
                      const dropdownRect = dropdownRef.current?.getBoundingClientRect();
                      const spaceBelow = window.innerHeight - (dropdownRect?.bottom || 0);
                      setOpenUpward(spaceBelow < 200);
                    }, 0);
                  }}
                  className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
                  aria-label="Actions"
                >
                  <ChevronDown className="h-5 w-5" />
                </button>

                {showRoleMenu && selectedUser?.id === user.id && (
                  <div className={`dropdown-menu absolute right-0 ${openUpward ? 'bottom-full mb-2' : 'mt-2'} w-56 origin-top-right rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}>
                    <div className="py-1">
                      <button
                        onClick={() => openEditModal(user)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => updateUserRole(user.id, 'agent')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Définir comme Agent
                      </button>
                      <button
                        onClick={() => updateUserRole(user.id, 'formateur')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Définir comme Formateur
                      </button>
                      <button
                        onClick={() => updateUserRole(user.id, 'admin')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Définir comme Admin
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={() => toggleUserStatus(user.id)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {user.is_active ? 'Désactiver' : 'Activer'} le compte
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                {user.role}
              </span>
              {user.is_active ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <UserCheck className="h-3 w-3 mr-1" />
                  Actif
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <UserX className="h-3 w-3 mr-1" />
                  Inactif
                </span>
              )}
              <span className="text-xs text-gray-500 ml-auto">
                Inscrit le {new Date(user.created_at).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Modal création */}
      {showCreateModal && (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/40">
          <div className="modal-container w-full max-w-lg bg-white rounded-xl shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Nouvel utilisateur</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prénom</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mot de passe</label>
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
                  <input
                    type="text"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'agent' | 'formateur' | 'admin' })}
                  disabled={currentUser?.role === 'formateur'}
                  className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="agent">Agent</option>
                  {currentUser?.role === 'admin' && (
                    <>
                      <option value="formateur">Formateur</option>
                      <option value="admin">Administrateur</option>
                    </>
                  )}
                </select>
                {currentUser?.role === 'formateur' && (
                  <p className="mt-1 text-sm text-gray-500">Les formateurs ne peuvent créer que des comptes agents</p>
                )}
              </div>

              {/* Actions compactes */}
              <div className="mt-6 sm:mt-8 sticky bottom-0 bg-white pb-2 pt-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal édition */}
      {showEditModal && selectedUser && (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/40">
          <div className="modal-container w-full max-w-lg bg-white rounded-xl shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Modifier l'utilisateur</h2>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prénom</label>
                  <input
                    type="text"
                    value={editFormData.prenom}
                    onChange={(e) => setEditFormData({ ...editFormData, prenom: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    value={editFormData.nom}
                    onChange={(e) => setEditFormData({ ...editFormData, nom: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
                  <input
                    type="text"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
                  <input
                    type="text"
                    value={editFormData.confirmPassword}
                    onChange={(e) => setEditFormData({ ...editFormData, confirmPassword: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {/* Actions compactes */}
              <div className="mt-6 sm:mt-8 sticky bottom-0 bg-white pb-2 pt-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
